// SubcontractorPOsPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  ConfigProvider, Table, Button, Select, Card,
  Modal, Input, Tooltip,
  Descriptions, Checkbox, notification, message
} from 'antd';
import {
  SyncOutlined, CheckOutlined,
  WarningOutlined, EditOutlined, EditFilled, DeleteOutlined,
  InfoOutlined, SearchOutlined, FilePdfOutlined
} from '@ant-design/icons';

import SideMenu from '../components/SideMenu';
import PurchaseOrderDetails from '../components/PurchaseOrderDetails';
import PurchaseOrderEdit from '../components/PurchaseOrderEdit';
import { StatusEditor, AmountEditor, PurchaseOrderList } from '../components/InvoiceAndPurchaseOrderComponents';
import { getFormattedDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/stringUtils';
import { useConstants } from '../context/ConstantsContext';
import { useEvents } from '../context/EventContext';
import {
  fetchPurchaseOrders,
  deleteSubcontractorPOs,
  approvePO,
  fetchPurchaseOrderPDF,
  synchronizePurchaseOrder,
  updatePurchaseOrderStatus,
  updatePurchaseOrderAmount
} from '../api';

function SubcontractorsPOPage() {
  const [messageApi, contextHolder] = message.useMessage();
  const constants = useConstants();
  const [constantsLoading, setConstantsLoading] = useState(true);
  const { allEvents, selectedEventId, selectedEvent, selectEvent, isLoading: eventsLoading } = useEvents();

  const [searchQuery, setSearchQuery] = useState('');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('po_id');
  const [filteredStatus, setFilteredStatus] = useState([]);

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [filteredPurchaseOrders, setFilteredPurchaseOrders] = useState([]);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [poPdfData, setPoPdfData] = useState(null);

  const [isApproving, setIsApproving] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedApprovePO, setSelectedApprovePO] = useState(null);
  const [approveAsDraft, setApproveAsDraft] = useState(false);

  const [loadingStates, setLoadingStates] = useState({});
  const [allSelectedKeys, setAllSelectedKeys] = useState(new Set());
  const [filteredRowKeys, setFilteredRowKeys] = useState([]);
  const [selectedPurchaseOrders, setSelectedPurchaseOrders] = useState([]);
  const [batchLoadingStates, setBatchLoadingStates] = useState({});
  const [batchMessages, setBatchMessages] = useState({});
  const [isBatchSyncModalOpen, setIsBatchSyncModalOpen] = useState(false);
  const [isBatchApproveModalOpen, setIsBatchApproveModalOpen] = useState(false);

  useEffect(() => {
    if (constants) {
      setConstantsLoading(false);
      setFilteredStatus([
        constants.status.pending,
        constants.status.incomplete,
        constants.status.awaiting,
        constants.status.approved,
      ]);
    }
  }, [constants]);

  // Initialize event selection from context
  useEffect(() => {
    if (allEvents.length > 0 && !selectedEventId) {
      // Select first event if none selected
      selectEvent(allEvents[0].event_id);
    }
  }, [allEvents, selectedEventId, selectEvent]);

  useEffect(() => {
    if (selectedEventId !== null) {
      const loadPurchaseOrders = async () => {
        try {
          const data = await fetchPurchaseOrders(selectedEventId);
          console.log("po data:", data);
          setPurchaseOrders(data);
        } catch (error) {
          console.error('Error fetching POs:', error);
        }
      };
      setAllSelectedKeys(new Set());
      setSelectedPurchaseOrders([]);
      loadPurchaseOrders();
    }
  }, [selectedEventId]);

  useEffect(() => {
    const filteredData = purchaseOrders.filter(purchaseOrder => {
      const subcontractorName = purchaseOrder.subcontractor_name || 'N/A';
      const routeName = purchaseOrder.route_name || 'N/A';
      const siteName = purchaseOrder.site_name || 'N/A';
  
      return (
        subcontractorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        routeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        siteName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
    
    setFilteredPurchaseOrders(filteredData);
  }, [searchQuery, purchaseOrders]);

  useEffect(() => {
    const currentFilteredKeys = filteredPurchaseOrders.map(po => po.key);
    setFilteredRowKeys([...allSelectedKeys].filter(key => currentFilteredKeys.includes(key)));
  }, [filteredPurchaseOrders, allSelectedKeys]);

  const onSelectChange = (newSelectedKeys) => {
    const updatedSelectedKeys = new Set(allSelectedKeys);
    newSelectedKeys.forEach(key => updatedSelectedKeys.add(key));
    filteredRowKeys.forEach(key => {
      if (!newSelectedKeys.includes(key)) updatedSelectedKeys.delete(key);
    });
    setAllSelectedKeys(updatedSelectedKeys);
    setFilteredRowKeys(newSelectedKeys);

    const selectedOrders = purchaseOrders.filter((po) => updatedSelectedKeys.has(po.key));
    setSelectedPurchaseOrders(selectedOrders);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedPurchaseOrders = [...filteredPurchaseOrders].sort((a, b) => {
    const aValue = a[orderBy] !== null && a[orderBy] !== undefined ? a[orderBy] : '';
    const bValue = b[orderBy] !== null && b[orderBy] !== undefined ? b[orderBy] : '';
  
    if (orderBy === 'po_id' || orderBy === 'precoro_idn') {
      return order === 'asc' ? aValue - bValue : bValue - aValue;
    } else {
      return order === 'asc'
        ? aValue.toString().localeCompare(bValue.toString())
        : bValue.toString().localeCompare(aValue.toString());
    }
  });

  // Format events for dropdown display
  const eventOptions = allEvents.map(event => ({
    label: `${event.event_name} - ${event.service_type}`,
    value: event.event_id,
  }));

  const handleEventChange = (value) => {
    selectEvent(value);
  };

  const handleDeleteClick = () => {
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteSubcontractorPOs([...allSelectedKeys]);
      await fetchPurchaseOrders(selectedEventId);
      setFilteredRowKeys([]);
      setAllSelectedKeys(new Set());
    } catch (error) {
      console.error('Error deleting POs:', error);
    } finally {
      setDeleteOpen(false);
    }
  };

  const handleStatusUpdate = (poId, newStatus) => {
    setPurchaseOrders((prevOrders) =>
      prevOrders.map((po) => (po.po_id === poId ? { ...po, status: newStatus } : po))
    );
  };

  const handleEditClick = (po) => {
    setSelectedPO(po);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setSelectedPO(null);
    setEditOpen(false);
  };

  const handlePOContractTypeUpdate = (poId, newContractType) => {
    setPurchaseOrders((prevPOs) =>
      prevPOs.map((po) =>
        po.po_id === poId ? { ...po, contract_type: newContractType } : po
      )
    );

    setSelectedPO((prevSelected) =>
      prevSelected && prevSelected.po_id === poId
        ? { ...prevSelected, contract_type: newContractType }
        : prevSelected
    );
  };

  const handleDetailClick = async (po) => {
    try {
      setSelectedPO(po);
      setDetailOpen(true);

      if (po.pdf_complete) {
        // // If result is a string (presigned URL), we can't render it in object tag  // TODO: Uncomment when re-enabling S3
        // // For now, set it to null and let the component handle it differently
        // const result = await fetchPurchaseOrderPDF(po.po_id);
        // if (typeof result === 'string') {
        //   setPoPdfData(null); // Will be handled by PurchaseOrderDetails component
        // } else {
        //   setPoPdfData(result);
        // }
        
        const blob = await fetchPurchaseOrderPDF(po.po_id);
        setPoPdfData(blob);
      } else {
        setPoPdfData(null);
      }
    } catch (error) {
      console.error('Error fetching purchase order details:', error);
    }
  };

  const handleDetailClose = () => {
    setDetailOpen(false);
    setSelectedPO(null);
    setPoPdfData(null);
  };

  const renderPDF = (pdfData) => {
    if (!pdfData) {
      return null;
    }
    const pdfUrl = URL.createObjectURL(pdfData);
    return (
      <object
        data={pdfUrl}
        type="application/pdf"
        width="100%"
        height="auto"
        style={{ marginTop: '20px', minHeight: '900px' }}
      >
        <p>Your browser does not support PDFs. Please download the PDF to view it: <a href={pdfUrl}>Download PDF</a>.</p>
      </object>
    );
  };
  
  const handleSyncPurchaseOrderClick = async (poId) => {
    setLoadingStates((prev) => ({ ...prev, [poId]: true }));
    try {
      const messages = await synchronizePurchaseOrder(poId);
      const data = await fetchPurchaseOrders(selectedEventId);
      setPurchaseOrders(data);


      notification.open({
        message: 'Sync Results',
        description: (
          <ul>
            {messages.map((msg, index) => (
              <li key={index}>
                {msg}
              </li>
            ))}
          </ul>
        ),
        duration: 0,
      });
    } catch (error) {
      messageApi.error(error.message || 'Failed to update purchase order amount & generate PDF');
    } finally {
      setLoadingStates((prev) => ({ ...prev, [poId]: false }));
    }
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const confirmBatchSync = async () => {
    const syncPromises = selectedPurchaseOrders.map(async (po, index) => {
      await sleep(index * 2000); // Delay by 2000ms per purchase order to stagger requests
      setBatchLoadingStates((prev) => ({ ...prev, [po.po_id]: true }));
      setBatchMessages((prev) => ({ ...prev, [po.po_id]: null }));
  
      try {
        const messages = await synchronizePurchaseOrder(po.po_id);
        const successMessage = messages.join('; ');
        setBatchMessages((prev) => ({ ...prev, [po.po_id]: successMessage }));
      } catch (error) {
        const errorMessage = error.message || `Failed to sync PO`;
        setBatchMessages((prev) => ({ ...prev, [po.po_id]: errorMessage }));
      } finally {
        setBatchLoadingStates((prev) => ({ ...prev, [po.po_id]: false }));
      }
    });
  
    await Promise.all(syncPromises);
    const updatedPurchaseOrders = await fetchPurchaseOrders(selectedEventId);
    setPurchaseOrders(updatedPurchaseOrders);
  };


  const handleBatchSyncModalClose = async () => {
    setBatchLoadingStates({});
    setBatchMessages({});
    setIsBatchSyncModalOpen(false);
  };
  
  
  const handleViewPDFClick = async (poId) => {
    const newTab = window.open(`/purchase-orders/${poId}/pdf`, '_blank');
    if (!newTab || newTab.closed || typeof newTab.closed === 'undefined') {
      messageApi.error('Pop-up blocked! Please allow pop-ups for this site to view the PDF.');
    }
  };

  const handleApproveClick = (po) => {
    setSelectedApprovePO(po);
    setApproveDialogOpen(true);
  };

  const confirmApprovePO = async () => {
    setIsApproving(true);

    try {
      const poApproveResponse = await approvePO(selectedApprovePO.po_id, approveAsDraft);
      const { message, errors } = poApproveResponse;

      if (errors && errors.length > 0) {
        messageApi.warning(`${message}: ${errors.join('; ')}`)
      } else {
        messageApi.success(message || 'Purchase Order Approved');
      }

      const data = await fetchPurchaseOrders(selectedEventId);
      setPurchaseOrders(data);
    } catch (error) {
      console.error('Failed to approve and post PO:', error);
      if (error.response && error.response.data && error.response.data.error) {
        messageApi.error(error.response.data.error);
      } else {
        messageApi.error('Failed to Approve Purchase Order');
      }
    } finally {
      setIsApproving(false);
      setSelectedApprovePO(null);
      setApproveAsDraft(false);
      setApproveDialogOpen(false);
    }
  };

  const confirmBatchApproval = async () => {
    const approvalPromises = selectedPurchaseOrders.map(async (po) => {
      setIsApproving(true);
      setBatchLoadingStates((prev) => ({ ...prev, [po.po_id]: true }));
      setBatchMessages((prev) => ({ ...prev, [po.po_id]: null }));
  
      try {
        const poApproveResponse = await approvePO(po.po_id, approveAsDraft);
        const { message, errors } = poApproveResponse;
  
        const responseMessage = errors && errors.length > 0 ? `${message}: ${errors.join('; ')}` : message || 'Approved successfully';
        setBatchMessages((prev) => ({ ...prev, [po.po_id]: responseMessage }));
      } catch (error) {
        const errorMessage = error.response?.data?.error || `Failed to approve PO`;
        setBatchMessages((prev) => ({ ...prev, [po.po_id]: errorMessage }));
      } finally {
        setIsApproving(false);
        setApproveAsDraft(false);
        setBatchLoadingStates((prev) => ({ ...prev, [po.po_id]: false }));
      }
    });
  
    await Promise.all(approvalPromises);
    const updatedPurchaseOrders = await fetchPurchaseOrders(selectedEventId);
    setPurchaseOrders(updatedPurchaseOrders);
  };


  const handleBatchApproveModalClose = async () => {
    setBatchLoadingStates({});
    setBatchMessages({});
    setApproveAsDraft(false);
    setIsBatchApproveModalOpen(false);
  };

  const updateAmount = async (poId, amount) => {
    setLoadingStates((prev) => ({ ...prev, [poId]: true }));
    try {
      await updatePurchaseOrderAmount(poId, amount);
      setPurchaseOrders((prevOrders) =>
        prevOrders.map((po) =>
          po.po_id === poId ? { ...po, custom_amount: amount } : po
        )
      );
      messageApi.success(`Purchase Order amount updated successfully`);
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || error.response?.message || 'Failed to update purchase order amount';
      messageApi.error(errorMessage);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [poId]: false }));
    }
  };

  const rowSelection = {
    selectedRowKeys: filteredRowKeys,
    onChange: onSelectChange,
  };

  const AmountColumn = ({ record, constants, updateAmount }) => {
    const [isEditIconVisible, setIsEditIconVisible] = useState(false);
  
    return (
      <div
        onMouseEnter={() => setIsEditIconVisible(true)}
        onMouseLeave={() => setIsEditIconVisible(false)}
      >
        <AmountEditor
          record={record}
          constants={constants}
          onAmountChange={(poId, newAmount) => updateAmount(poId, newAmount)}
          isEditIconVisible={isEditIconVisible}
        />
      </div>
    );
  };

  const columns = [
    {
      title: (
        <Tooltip title="Assigned when purchase order is approved.">
          <span>ID #</span>
        </Tooltip>
      ),
      dataIndex: "precoro_idn",
      key: "precoro_idn",
      width: '8%',
      sorter: true,
      sortOrder: orderBy === 'precoro_idn' && order,
      onHeaderCell: () => ({ onClick: () => handleRequestSort('po_id') }),
    },
    {
      title: "Subcontractor",
      dataIndex: "subcontractor_name",
      key: "subcontractor_name",
      width: '20%',
      sorter: true,
      sortOrder: orderBy === 'subcontractor_name' && order,
      onHeaderCell: () => ({ onClick: () => handleRequestSort('subcontractor_name') }),
    },
    {
      title: "Site",
      dataIndex: "site_name",
      key: "site_name",
      width: '20%',
      render: (site_name) => site_name || '-',
      sorter: true,
      sortOrder: orderBy === 'site_name' && order,
      onHeaderCell: () => ({ onClick: () => handleRequestSort('site_name') }),
    },
    {
      title: "Route",
      dataIndex: "route_name",
      key: "route_name",
      width: '20%',
      sorter: true,
      sortOrder: orderBy === 'route_name' && order,
      onHeaderCell: () => ({ onClick: () => handleRequestSort('route_name') }),
    },
    {
      title: "Amount",
      dataIndex: "total_amount_sum",
      width: '10%',
      key: "total_amount_sum",
      render: (total_amount_sum, record) => (
        <AmountColumn
          record={record}
          constants={constants}
          updateAmount={updateAmount}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      filters: [
        { text: 'Pending', value: constants?.status.pending },
        { text: 'Incomplete', value: constants?.status.incomplete },
        { text: 'Awaiting Approval', value: constants?.status.awaiting },
        { text: 'Approved', value: constants?.status.approved },
        { text: 'Cancelled', value: constants?.status.cancelled },
      ],
      filteredValue: filteredStatus,
      onFilter: (value, record) => {
        return record.status === value;
      },
      render: (status, record) => (
        <StatusEditor 
          status={status}
          record={record} 
          statusUpdateFunction={updatePurchaseOrderStatus} 
          statusType="Purchase Order"
          handleStatusUpdate={handleStatusUpdate}
          messageApi={messageApi}
        />
      )
    },
    {
      title: "Precoro",
      dataIndex: "precoro_supplier_id",
      key: "precoro_supplier_id",
      align: "center",
      width: "9%",
      render: (precoro_supplier_id) => {
        if (precoro_supplier_id === null) {
          return (
            <Tooltip title="Subcontractor is not matched to a Precoro Supplier">
              <WarningOutlined style={{ color: 'orange', fontSize: '18px' }} />
            </Tooltip>
          );
        } else {
          return (
            <Tooltip title="Subcontractor matched to Precoro Supplier">
              <CheckOutlined style={{ color: 'green', fontSize: '18px' }} />
            </Tooltip>
          );
        }
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record) => (
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2px'
          }}
        >
          <Tooltip title="PO Details">
            <Button
              shape='circle'
              icon={<InfoOutlined />}
              onClick={() => handleDetailClick(record)}
            />
          </Tooltip>
          
          <Tooltip title="Edit PO">
            <Button
              shape='circle'
              icon={<EditOutlined />}
              onClick={() => handleEditClick(record)}
              disabled={record.status === constants.status.approved}
            />
          </Tooltip>

          <Tooltip title="Recalculate and generate the latest PDF for this PO">
            <Button
              shape='circle'
              icon={
                <SyncOutlined
                  style={{ color: record.status === constants.status.approved ? '#d9d9d9' : '#1890ff' }}
                  spin={loadingStates[record.po_id]}
                />
              }
              onClick={() => handleSyncPurchaseOrderClick(record.po_id)}
              disabled={record.status === constants.status.approved || loadingStates[record.po_id]}
            />
          </Tooltip>

          <Tooltip title="View PO PDF">
            <Button
              shape='circle'
              icon={<FilePdfOutlined />}
              onClick={() => handleViewPDFClick(record.po_id)}
              disabled={!record.pdf_complete}
            />
          </Tooltip>

          <Tooltip title="This PO is out of date with its service histories">
            <WarningOutlined
              style={{
                color: 'orange',
                fontSize: '18px',
                visibility: (!record.up_to_date && record.pdf_complete) ? 'visible' : 'hidden'
              }}
            />
          </Tooltip>

          <Tooltip title={`Contract type mismatch: Synced with "${record.synced_contract_type}" but currently set to "${record.contract_type}". Please resync.`}>
            <WarningOutlined
              style={{
                color: 'red',
                fontSize: '18px',
                visibility: (record.synced_contract_type && record.contract_type !== record.synced_contract_type) ? 'visible' : 'hidden'
              }}
            />
          </Tooltip>

          <Tooltip
            title={
              record.status === constants.status.approved
                ? "PO Approved"
                : record.status === constants.status.pending
                ? "Please sync PO before approval"
                : "Approve PO"
            }
            placement="top"
          >
            <span>
              {record.status !== constants.status.pending ? (
                <Button
                  shape='circle'
                  icon={
                    record.status === constants.status.awaiting ? (
                      <CheckOutlined style={{ color: '#F57D20' }} />
                    ) : (
                      <CheckOutlined style={{ color: 'green' }} />
                    )}
                  onClick={() => handleApproveClick(record)}
                  disabled={record.status !== constants.status.awaiting}
                />
              ) : (
                <Button
                  shape='circle'
                  disabled
                  icon={<CheckOutlined style={{ color: 'grey' }} />}
                />
              )}
            </span>
          </Tooltip>
        </Box>
      ),
      align: 'center',
    },
  ];

  const [tableHeight, setTableHeight] = useState('100vh');
  const titleBoxRef = useRef(null);
  const eventDetailsRef = useRef(null);
  const actionBoxRef = useRef(null);
  const tableRef = useRef(null);

  useEffect(() => {
    const calculateTableHeight = () => {
      const headerHeight = 72;
      const titleBoxHeight = titleBoxRef.current ? titleBoxRef.current.offsetHeight : 32;
      const actionBoxHeight = actionBoxRef.current ? actionBoxRef.current.offsetHeight : 32;
      const eventDetailsHeight = eventDetailsRef.current ? eventDetailsRef.current.offsetHeight + 20: 74;
      const padding = 16;

      // Calculate height of the table header
      const tableHeaderHeight = tableRef.current ? tableRef.current.querySelector('.ant-table-thead').offsetHeight : 48;

      const totalHeightAroundTableRows =
        headerHeight +
        titleBoxHeight +
        actionBoxHeight +
        eventDetailsHeight +
        padding * 6 +
        tableHeaderHeight;

      setTableHeight(`calc(100vh - ${totalHeightAroundTableRows}px)`);
    };

    if (selectedEvent) {
        calculateTableHeight();
    }

    window.addEventListener('resize', calculateTableHeight);
    return () => window.removeEventListener('resize', calculateTableHeight);
  }, [selectedEvent]);

  if (constantsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#F57D20', colorLinkHover: '#F57D20', colorLink: '#F57D20' } }}>
      {contextHolder}
      <Box display="flex">
        {/* Side Menu */}
        <Box sx={{ width: '250px', flexShrink: 0 }}>
          <SideMenu />
        </Box>
        
        {/* Main Content */}
        <Card
          style={{
            height: 'calc(100vh - 72px - 16px)',
            marginRight: '16px',
            overflow: 'hidden',
            boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)'
          }}
        >

          {/* Header and Event Selection */}
          <Box ref={titleBoxRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>
            <Typography variant="h5" component="div">
              Subcontractor Purchase Orders
            </Typography>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Select
                value={selectedEventId}
                onChange={handleEventChange}
                showSearch
                placeholder="Select Event"
                options={eventOptions}
                style={{ width: '320px' }}
                optionFilterProp="label"
                loading={eventsLoading}
              />
            </Box>
          </Box>

          {/* Event Details */}
          {selectedEvent && (
            <Box
              ref={eventDetailsRef}
              marginTop="16px"
              padding="10px"
              border="1px solid #ddd"
              borderRadius="8px"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              backgroundColor="#f9f9f9"
              boxShadow="0px 2px 8px rgba(0, 0, 0, 0.1)"
            >
              <Typography variant="h6" component="div" style={{ fontWeight: '500', color: '#333' }}>
                {selectedEvent.event_name} - {selectedEvent.service_type}
              </Typography>
              <Typography variant="body1" component="div" style={{ color: '#666' }}>
                {getFormattedDate(selectedEvent.event_start)}
              </Typography>
            </Box>
          )}

          {/* Search and Action Buttons */}
          <Box ref={actionBoxRef} display="flex" justifyContent="space-between" alignItems="center" marginTop="16px">
            <Input
              type="text"
              placeholder="Search Purchase Orders"
              value={searchQuery}
              onChange={handleSearchChange}
              style={{ width: '250px' }}
              prefix={<SearchOutlined style={{ color: "#d9d9d9" }} />}
            />
            
            <Box display="flex" gap="10px">
              <Tooltip title="Delete Selected POs">
                <Button
                  style={{ color: '#FF6961' }}
                  onClick={handleDeleteClick}
                  disabled={allSelectedKeys.length === 0}
                >
                  <DeleteOutlined />
                </Button>
              </Tooltip>
              <Tooltip title="Sync Selected POs">
                <Button onClick={() => setIsBatchSyncModalOpen(true)}>
                  Sync <SyncOutlined />
                </Button>
              </Tooltip>
              <Tooltip title="Approve Selected POs">
                <Button type="primary" onClick={() => setIsBatchApproveModalOpen(true)}>
                  Approve <CheckOutlined />
                </Button>
              </Tooltip>
            </Box>
          </Box>

          {/* Scrollable Table */}
          <Box ref={tableRef} sx={{ overflow: 'hidden', marginTop: '16px' }}>
            <Table
              columns={columns}
              dataSource={sortedPurchaseOrders}
              rowKey={(record) => record.po_id}
              rowSelection={rowSelection}
              pagination={false}
              scroll={{
                x: 'max-content',
                y: tableHeight,
              }}
              onChange={(pagination, filters) => {
                if (filters.status) {
                  setFilteredStatus(filters.status);
                }
              }}
              sticky
            />
          </Box>
        </Card>
      </Box>

      <Modal 
        open={deleteOpen} 
        onClose={handleDeleteClose} 
        title="Confirm Delete" 
        centered 
        onCancel={handleDeleteClose} 
        footer={[
          <Button key='cancel-po-deletion' onClick={handleDeleteClose}>Close</Button>, 
          <Button key='confirm-po-deletion' onClick={handleConfirmDelete} type="primary">Delete</Button>
        ]}>
        Are you sure you want to delete the selected POs? This action cannot be undone.
      </Modal>

      <Modal
        open={detailOpen}
        onCancel={handleDetailClose}
        footer={null}
        width='60%'
      >
        <PurchaseOrderDetails purchaseOrder={selectedPO} />
        {renderPDF(poPdfData)}
      </Modal>

      <Modal
        open={editOpen}
        onCancel={handleEditClose}
        footer={null}
        width="60%"
      >
        {selectedPO && (
          <PurchaseOrderEdit
            poId={selectedPO.po_id}
            messageApi={messageApi}
            onContractTypeChange={handlePOContractTypeUpdate}
          />
        )}
      </Modal>

      {/* Approve Confirmation Modal */}
      <Modal
        title="Confirm Approval"
        open={approveDialogOpen}
        onOk={confirmApprovePO}
        onCancel={() => {
          setApproveDialogOpen(false);
          setApproveAsDraft(false);
        }}
        confirmLoading={isApproving}
        footer={[
          <Checkbox
            key="draft-checkbox"
            checked={approveAsDraft}
            onChange={(e) => setApproveAsDraft(e.target.checked)}
            style={{ float: 'left', lineHeight: '32px', marginRight: 'auto' }}
          >
            Approve as Draft
          </Checkbox>,
          <Button
            key="cancel"
            onClick={() => {
              setApproveDialogOpen(false);
              setApproveAsDraft(false);
            }}
          >
            Cancel
          </Button>,
          <Button key="submit" type="primary" loading={isApproving} onClick={confirmApprovePO}>
            Approve
          </Button>,
        ]}
      >
        <Descriptions
          column={1}
          size="small"
          bordered
        >
          <Descriptions.Item label="Event Name">{selectedApprovePO?.event_name}</Descriptions.Item>
          <Descriptions.Item label="Event Date">{getFormattedDate(selectedApprovePO?.event_date)}</Descriptions.Item>
          <Descriptions.Item label="Subcontractor">{selectedApprovePO?.subcontractor_name}</Descriptions.Item>
          <Descriptions.Item label="Service Type">{selectedApprovePO?.service_type}</Descriptions.Item>
          <Descriptions.Item label="Contract Type">{selectedApprovePO?.contract_type || 'N/A'}</Descriptions.Item>
          { selectedApprovePO?.country === constants.country.canada &&
            <>
              <Descriptions.Item label="Net Total">{formatCurrency(selectedApprovePO.custom_amount ?? selectedApprovePO.total_amount_sum, selectedApprovePO.country, constants)}</Descriptions.Item>
              <Descriptions.Item label={`GST ${constants.taxes.cad_tax_rate * 100}%`}>{formatCurrency((selectedApprovePO.custom_amount ?? selectedApprovePO.total_amount_sum) * constants.taxes.cad_tax_rate, selectedApprovePO.country, constants)}</Descriptions.Item>
              <Descriptions.Item label="Gross Total">
                {formatCurrency((selectedApprovePO.custom_amount ?? selectedApprovePO.total_amount_sum) * (1 + constants.taxes.cad_tax_rate), selectedApprovePO.country, constants)}
                {selectedApprovePO.custom_amount && <EditFilled style={{ marginLeft: '8px', fontSize: '0.9em', color: '#F57D20' }} />}
              </Descriptions.Item>
            </>
          }
          { selectedApprovePO?.country === constants.country.usa &&
            <Descriptions.Item label="Gross Total">
              {formatCurrency(selectedApprovePO.custom_amount ?? selectedApprovePO.total_amount_sum, selectedApprovePO.country, constants)}
              {selectedApprovePO.custom_amount && <EditFilled style={{ marginLeft: '8px', fontSize: '0.9em', color: '#F57D20' }} />}
            </Descriptions.Item>
          }
        </Descriptions>
      </Modal>

      {/* Batch Sync Confirmation Modal */}
      <Modal
        title="Confirm Batch Sync"
        open={isBatchSyncModalOpen}
        okText="Sync"
        onOk={confirmBatchSync}
        onCancel={handleBatchSyncModalClose}
        width="60%"
      >
        <p>You are about to sync the following Purchase Orders:</p>
        <PurchaseOrderList
          purchaseOrders={selectedPurchaseOrders}
          batchMessages={batchMessages}
          batchLoadingStates={batchLoadingStates}
          constants={constants}
        />
        <p>This will update their service histories, recalculate amounts, and generate PDFs.</p>
      </Modal>

      {/* Batch Approve Confirmation Modal */}
      <Modal
        title="Confirm Batch Approve"
        open={isBatchApproveModalOpen}
        onCancel={handleBatchApproveModalClose}
        width="60%"
        footer={[
          <Checkbox
            key="draft-checkbox"
            checked={approveAsDraft}
            onChange={(e) => setApproveAsDraft(e.target.checked)}
            style={{ float: 'left', lineHeight: '32px', marginRight: 'auto' }}
          >
            Approve as Draft
          </Checkbox>,
          <Button key="cancel" onClick={handleBatchApproveModalClose}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" loading={isApproving} onClick={confirmBatchApproval}>
            Approve
          </Button>,
        ]}
      >
        <p>You are about to approve the following Purchase Orders:</p>
        <PurchaseOrderList
          purchaseOrders={selectedPurchaseOrders}
          batchMessages={batchMessages}
          batchLoadingStates={batchLoadingStates}
          constants={constants}
        />
      </Modal>
    </ConfigProvider>
  );
};

export default SubcontractorsPOPage;